/**
 * designTokens — source de vérité visuelle du dashboard frontend.
 *
 * Aligné sur docs/DESIGN_SYSTEM.md, mobile/features/shared/graphConfig.ts
 * et mobile/shared/types/status.ts.
 *
 * Toute modification doit être propagée à ces fichiers et à
 * hub/web/static/history.js (couleurs graphiques).
 */

export type StatusKey = 'ok' | 'warning' | 'alarm' | 'offline' | 'uncalibrated' | 'unknown';

export interface StatusColorSet {
  bg: string;
  text: string;
  badge: string;
  border: string;
}

export const STATUS_COLORS: Record<StatusKey, StatusColorSet> = {
  ok: {
    bg: '#dcfce7',    // green-100
    text: '#166534',  // green-800
    badge: '#22c55e', // green-500
    border: '#bbf7d0', // green-200
  },
  warning: {
    bg: '#fef9c3',    // yellow-100
    text: '#854d0e',  // yellow-800
    badge: '#f59e0b', // amber-500
    border: '#fef08a', // yellow-200
  },
  alarm: {
    bg: '#fee2e2',    // red-100
    text: '#991b1b',  // red-800
    badge: '#ef4444', // red-500
    border: '#fecaca', // red-200
  },
  offline: {
    bg: '#f3f4f6',    // gray-100
    text: '#374151',  // gray-700
    badge: '#9ca3af', // gray-400
    border: '#e5e7eb', // gray-200
  },
  uncalibrated: {
    bg: '#fef3c7',    // amber-100
    text: '#92400e',  // amber-800
    badge: '#f59e0b', // amber-500
    border: '#fde68a', // amber-200
  },
  unknown: {
    bg: '#f3f4f6',    // gray-100
    text: '#4b5563',  // gray-600
    badge: '#6b7280', // gray-500
    border: '#e5e7eb', // gray-200
  },
};

/**
 * Couleurs des courbes de graphique température.
 * Miroir exact de mobile/features/shared/graphConfig.ts → GRAPH_COLORS.
 */
export const GRAPH_COLORS = {
  curveOfficial: '#3b82f6', // blue-500 — courbe officielle (trait plein)
  curveAdv:      '#93c5fd', // blue-300 — courbe advertising (pointillé)
  alarmThreshold: '#ef4444', // red-500  — lignes seuils alarme
  warningThreshold: '#f59e0b', // amber-500 — lignes seuils warning (si affichées)
  axis:          '#9ca3af', // gray-400
  gridMajor:     '#e0e0e0',
  gridMinor:     '#efefef',
} as const;

/**
 * Styles des courbes de graphique température.
 * Miroir exact de mobile/features/shared/graphConfig.ts → GRAPH_CURVE.
 */
export const GRAPH_CURVE = {
  officialWidth:  2,
  advWidth:       1,
  advDash:        [6, 4] as [number, number],
  thresholdWidth: 1.5,
  thresholdDash:  [5, 5] as [number, number],
} as const;
